// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@rc/XDL/Utilities";
import isMobile from "ismobilejs";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const pageType: any = getCurrentPage(state).type;
    return {
        isAuthenticated: getSession(state).isAuthenticated && !getSession(state)?.isGuest,
        isCheckoutPages:
            pageType === "CheckoutShippingPage" ||
            pageType === "CheckoutReviewAndSubmitPage" ||
            pageType === "OrderConfirmationPage" ||
            pageType === "RfqRequestQuotePage" ||
            pageType === "RfqConfirmationPage",
    };
};

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    topContentGridItem?: GridItemProps;
    bottomContentGridItem?: GridItemProps;
    topContentGridContainer?: GridContainerProps;
    bottomContentGridContainer?: GridContainerProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    linkList5GridItem?: GridItemProps;
    richContent2GridItem?: GridItemProps;
    supportServicesGridItem?: GridItemProps;
    richContent2Wrapper?: InjectableCss;
    copyrightText?: TypographyPresentationProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            a,
            span[class^="StyledButton"] {
                span {
                    font-weight: 300;
                    color: ${getColor("common.ui1")} !important;
                    &:focus,
                    &:active,
                    &:hover {
                        color: ${getColor("primary.main")} !important;
                    }
                }
            }
        `,
    },
    topContentGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    topContentGridContainer: {
        gap: 22,
        css: css`
            &&&&& {
                margin: 32px 0 20px;
                ${xScreen("lte", "xs")} {
                    [class^="GridItemStyle"] {
                        padding: 16px 8px;
                    }
                }
            }
        `,
    },
    bottomContentGridItem: {
        width: 12,
    },
    bottomContentGridContainer: {
        css: css`
            > div {
                flex-direction: column;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 4, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 4, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 4, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 4, 4, 2, 2],
    },
    linkList5GridItem: {
        width: [6, 4, 4, 2, 2],
    },
    supportServicesGridItem: {
        width: [6, 4, 4, 2, 2],
    },
    richContent2GridItem: {
        width: 12,
        css: css`
            color: ${getColor("common.ui1")};
            font-size: 11px;
            letter-spacing: 0;
            line-height: 20px;
            text-align: center;
            padding-top: 20px;
            padding-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.ui1")};
            margin: 0 15px 15px !important;
            justify-content: center;
        `,
    },
    richContent2Wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 25px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                gap: 8px;
            }
        `,
    },
    copyrightText: {
        transform: "uppercase",
        size: 11,
        css: css`
            text-wrap: nowrap;
            font-weight: 400;
        `,
    },
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const FooterContainer: FC<Props> = ({ id, extendedStyles, isAuthenticated, isCheckoutPages }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    // XNG-Changes: copyright date should be dynamic
    const copyrightMessage = () => {
        const message = (siteMessage("Custom_Footer_Copyright") as string) || "© Royal Canin SAS {0}";
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    return (
        <GridContainer {...styles.widgetGridContainer}>
            <GridItem {...styles.topContentGridItem}>
                {!isCheckoutPages && (
                    <GridContainer {...styles.topContentGridContainer}>
                        <GridItem {...styles.linkList1GridItem}>
                            <Zone zoneName="LinkList1" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList2GridItem}>
                            <Zone zoneName="LinkList2" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList3GridItem}>
                            <Zone zoneName="LinkList3" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList4GridItem}>
                            <Zone zoneName="LinkList4" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList5GridItem}>
                            <Zone zoneName="LinkList5" contentId={id} />
                        </GridItem>

                        <GridItem {...styles.supportServicesGridItem}>
                            <Zone zoneName="RichContent1" contentId={id} />
                        </GridItem>
                    </GridContainer>
                )}
            </GridItem>
            <GridItem {...styles.bottomContentGridItem}>
                <GridContainer {...styles.bottomContentGridContainer}>
                    <GridItem {...styles.richContent2GridItem}>
                        <StyledWrapper {...styles.richContent2Wrapper}>
                            <Typography as="p" {...styles.copyrightText}>
                                {copyrightMessage()}
                            </Typography>
                            <Zone zoneName="RichContent2" contentId={id} />
                        </StyledWrapper>
                    </GridItem>
                    <Zone zoneName="RichContent3" contentId={id} />
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const footerContainer: WidgetModule = {
    component: connect(mapStateToProps)(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
